/**
 * Diagnose.jsx
 * Dialog NUR fuer Super-Admins: der Zustand der Hintergrund-Aufträge auf einen
 * Blick.
 *
 * ── Warum es diesen Dialog gibt ─────────────────────────────────────────────
 * `/api/admin/diag/jobs` liefert seit jeher den Zustand aller Aufträge, aber
 * die Oberfläche zeigte davon genau zwei: den Jahresverlauf-Worker und das
 * Archiv-Neueinlesen. Alles andere stand nur im rohen JSON, das jemand von Hand
 * aufrufen musste.
 *
 * Folge, gemessen am 05.10.2026: `syncRecentUpdates` stand **zwölf Tage** auf
 * Fehler (Zendesks 1000er-Grenze, der Auftrag konnte sich nicht selbst
 * erholen). In dieser Zeit wurden keine späten Änderungen mehr in die
 * Altmonats-Archive nachgetragen — rund vierzig Tickets in Juli und August
 * standen fälschlich auf „gelöst" statt „geschlossen". Niemand konnte es sehen.
 *
 * Dieser Dialog zeigt nichts Neues. Er zeigt, was ohnehin da war.
 *
 * ── Was hier NICHT passiert ─────────────────────────────────────────────────
 * Keine Knöpfe, kein Auslösen, kein Schreiben. Reine Anzeige. Wer einen
 * Auftrag von Hand starten will, nimmt die bestehenden Einträge im Benutzer-
 * menü — die sind absichtlich eine engere Rollenstufe und bleiben, wo sie sind.
 */
import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Nur die BESCHRIFTUNG, in der Reihenfolge des Takts.
 *
 * Takt und Zeitfenster standen hier frueher als dritte Spalte daneben und
 * wurden nie angezeigt — der Server liefert sie (`_auftragsplan`). Der tote
 * Text war trotzdem schaedlich: er behauptete „20 Min" fuer einen Auftrag, der
 * alle 15 laeuft, und fuehrte jeden in die Irre, der diese Datei liest.
 * Entfernt am 06.10.2026.
 *
 * Diese Liste ist KEINE Quelle dafuer, welche Auftraege es gibt — das sagt der
 * Server. Was er schickt und hier nicht steht, erscheint trotzdem, unter seinem
 * technischen Namen (siehe unten). Vorher verschwand es wortlos.
 */
const BESCHRIFTUNG = {
  syncStroeme:                  'Ereignis- und Ticket-Strom',
  syncAgentData:                'Agenten-Daten',
  syncCurrentWeekSnapshot:      'Laufende Woche',
  syncRecentUpdates:            'Nachträge in Altmonate',
  syncSupplementaryTeamTickets: 'Ergänzende Team-Tickets',
  syncHistory:                  'Monats-Archive',
  syncAgentenGedaechtnis:       'Gruppen-Gedächtnis',
  trendWeekly:                  'Jahresverlauf',
  rebuildArchive:               'Archiv neu bauen',
  reenrichArchives:             'Archive neu anreichern',
  recentUpdates:                'Nachträge anstossen',
  // Hier standen bis zum 13.10.2026 'Aktive Audits' (syncActiveAudits) und
  // 'Geschlossene Audits' (syncAudits). Die Audit-Welt ist entfernt — der
  // Server schickt die beiden Namen nicht mehr.
  syncFCR:                      'FCR-Vorberechnung',
  syncTrendWeekly:              'Wochen-Vorberechnung',
};

/** Reihenfolge: bekannte zuerst wie oben, Unbekanntes hinten dran. */
const REIHENFOLGE = Object.keys(BESCHRIFTUNG);

/**
 * Welche Zeilen die Tabelle zeigt — [Schluessel, Beschriftung].
 *
 * Alles, was der Server als Auftrag schickt, erscheint. Die Beschriftung oben
 * bestimmt nur Name und Reihenfolge; ein Auftrag ohne Eintrag landet hinten mit
 * seinem technischen Namen, statt wortlos zu verschwinden.
 *
 * Genau das ist am 06.10.2026 aufgefallen: `syncAgentenGedaechtnis` lief
 * taeglich, fehlte aber in der Namensliste des Endpunkts — und weil die alte
 * Fassung hier `if (!j) return null` machte, sah niemand etwas. Die Anzeige war
 * still an einer Stelle, an der sie haette reden muessen.
 */
function alleEintraege(daten) {
  if (!daten) return [];
  const istEintrag = (k, v) => !k.startsWith('_') && v && typeof v === 'object' && 'status' in v;
  const vorhanden  = Object.keys(daten).filter(k => istEintrag(k, daten[k]));
  const bekannt    = REIHENFOLGE.filter(k => vorhanden.includes(k));
  const unbekannt  = vorhanden.filter(k => !REIHENFOLGE.includes(k)).sort();
  return [...bekannt, ...unbekannt];
}

/**
 * Die geplanten Auftraege — alles, was im Taktplan steht.
 *
 * Getrennt von den Admin-Knoepfen, weil „nie gelaufen" bei beiden etwas
 * voellig anderes bedeutet. Bei einem Knopf heisst es: niemand hat ihn
 * gedrueckt. Bei einem Auftrag hiesse es: er faellt aus — und das waere bei
 * syncFCR und syncTrendWeekly schlicht falsch. Die laufen, sie
 * werden nur nicht gemessen (begruendet in worker/index.js: ihr 15-Minuten-
 * Tick erzeugt drei Leerlaeufe je Arbeitslauf und wuerde die eine
 * aussagekraeftige Messung sofort ueberschreiben).
 *
 * Vor dem 06.10.2026 fehlten sie ganz. Dann erschienen sie als „nie
 * gelaufen" — ein Verschweigen gegen eine Falschaussage getauscht. Beides
 * schlecht, deshalb jetzt die Unterscheidung.
 */
function auftragsZeilen(daten) {
  const plan = daten?._auftragsplan || {};
  const ruht = new Set(daten?._stillgelegt || []);
  return alleEintraege(daten)
    .filter(k => plan[k] || ruht.has(k))
    .map(k => [k, BESCHRIFTUNG[k] || k]);
}

/** Die von Hand ausgeloesten Laeufe — kein Takt, kein Ausfall moeglich. */
function knopfZeilen(daten) {
  const plan = daten?._auftragsplan || {};
  const ruht = new Set(daten?._stillgelegt || []);
  return alleEintraege(daten)
    .filter(k => !plan[k] && !ruht.has(k))
    .map(k => [k, BESCHRIFTUNG[k] || k]);
}

/**
 * Bewusst abgeschaltet — nicht ausgefallen.
 *
 * Ein stillgelegter Auftrag muss anders aussehen als ein ungemessener und als
 * ein ausgefallener. Drei Zustaende, drei Worte. Am 06.10. wurden zwei davon
 * verwechselt, und das Ergebnis war schlechter als Schweigen.
 */
const istStillgelegt = (daten, key) => (daten?._stillgelegt || []).includes(key);

/**
 * Laeuft der Auftrag, ohne gemessen zu werden?
 *
 * Er steht im Taktplan (also registriert und laufend), hat aber keinen
 * einzigen Zeitstempel. Das ist nicht „nie gelaufen", sondern „nie
 * aufgezeichnet".
 */
const nurUngemessen = (j) => j && j.status === 'idle' && !j.startedAt && !j.finishedAt;

const alterMin = (iso) => (iso ? Math.round((Date.now() - new Date(iso).getTime()) / 60000) : null);

/**
 * Alle stillen Rueckfaelle auf den Blob, zusammengezaehlt.
 *
 * Bewusst ueber ALLE Live-Wege statt ueber eine feste Liste: hier standen bis
 * zum 13.10.2026 genau zwei Summanden fest eingetragen, und als U11c den
 * dritten brachte, haette ein neuer Rueckfall die Zeile nicht bewegt. Die
 * Zeile ist die wichtigste der ganzen Seite — sie muss 0 bleiben.
 */
function rueckfaelle(z) {
  if (!z) return 0;
  return Object.values(z)
    .filter(v => v && typeof v === 'object' && typeof v.rueckfall === 'number')
    .reduce((n, v) => n + v.rueckfall, 0);
}

/**
 * Was ein von Hand ausgeloester Lauf GETAN hat — nicht nur, dass er lief.
 *
 * Der Anlass (13.10.2026): „Jahresverlauf" wurde am 09.10. gedrueckt, lief
 * 13 Sekunden, meldete „done" — und schrieb keine der 17 fehlenden Wochen.
 * Der Grund stand im Rueckgabewert (`berechnet: 0, uebersprungen: 17`), nur
 * reichte der Endpunkt ihn nicht weiter und die Anzeige zeigte ihn nicht.
 * Ein Knopf, der „fertig" sagt und verschweigt, dass er nichts getan hat, ist
 * schlimmer als einer ohne Rueckmeldung: er sieht aus wie Erfolg.
 *
 * Bewusst allgemein gehalten — die Auftraege melden verschiedene Felder, und
 * eine feste Liste hier waere die naechste Stelle, die beim naechsten neuen
 * Feld stillschweigend veraltet.
 */
function ergebnisText(result) {
  if (!result || typeof result !== 'object') return '';
  const teile = Object.entries(result)
    .filter(([k, v]) => typeof v === 'number' && k !== 'ms' && !k.endsWith('Ms'))
    .map(([k, v]) => `${k} ${v}`);
  return teile.length ? ` · ${teile.join(' · ')}` : '';
}

function alterText(min) {
  if (min === null) return 'nie gelaufen';
  if (min < 60)  return `vor ${min} Min`;
  if (min < 1440) return `vor ${Math.round(min / 60)} Std`;
  return `vor ${Math.round(min / 1440)} Tagen`;
}

/** Ortszeit, sekundengenau — zum Abgleich mit Grafana. */
const uhr = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  : '–');

function dauerText(von, bis) {
  if (!von || !bis) return '–';
  const ms = new Date(bis).getTime() - new Date(von).getTime();
  if (ms < 0) return '–';
  return msText(ms);   // EINE Formatierung, nicht zwei nebeneinander
}

/**
 * Minuten, ohne eine Zahl zu erfinden.
 *
 * `Math.round` machte aus 2,5 Minuten „3 Min" — und damit aus einem Takt von
 * 5 Minuten plus 2,5 Versatz scheinbar „5 + 3". Bei groben Takten faellt das
 * nicht auf, bei feinen schon (Fund 07.10.2026, beim Wechsel auf 5 Minuten).
 */
function minText(ms) {
  const m = ms / 60000;
  return Number.isInteger(m) ? `${m}` : m.toFixed(1).replace('.', ',');
}

function taktText(plan) {
  if (!plan) return null;
  const m = Math.round(plan.intervalMs / 60000);
  const takt = m < 60 ? `${minText(plan.intervalMs)} Min` : m === 60 ? 'stündlich' : `alle ${Math.round(m / 60)} Std`;
  const versatz = plan.initialDelayMs ? `, +${minText(plan.initialDelayMs)} Min Versatz` : '';
  return `${takt}, ${plan.zeitfenster}${versatz}`;
}

/**
 * Welchen ANTEIL seines Takts verbraucht ein Auftrag?
 *
 * Die Zahl, an der sich entscheidet, ob man ihn enger stellen kann — und damit
 * das Dashboard aktueller macht. Was sie NICHT abbildet: die tatsaechliche Last
 * auf dem Pod. Mehrere Auftraege koennen sich trotz Versatz ueberschneiden, und
 * CPU-Druck steht hier nirgends. Dafuer sind die Zeitstempel da: mit ihnen
 * laesst sich in Grafana nachsehen, was zu genau diesen Minuten los war.
 */
/**
 * Arbeit und Warten getrennt.
 *
 * Die Auftraege, die auf den Monatsarchiven arbeiten, laufen unter einer
 * gemeinsamen Sperre (`heavyJobLock`) — sie koennen sich also nicht
 * gegenseitig den Pod wegnehmen. Die Wanduhr misst damit zum grossen Teil
 * WARTESCHLANGE, nicht Aufwand.
 *
 * Gemessen am 05.10.2026: "Agenten-Daten, 64 s" — davon 61 s Warten und 3 s
 * Arbeit. Wer daraus auf die Kosten schliesst, liegt um den Faktor zwanzig
 * daneben. Fuer die Frage "koennen wir den Takt enger stellen" zaehlt die
 * Arbeit.
 *
 * `wartenMs`/`arbeitMs` liefert `trackedExclusive`; Auftraege ohne Sperre
 * (z.B. syncStroeme) haben sie nicht — dort gilt die Wanduhr.
 */
function zeiten(j) {
  const wanduhr = (j?.startedAt && j?.finishedAt)
    ? new Date(j.finishedAt).getTime() - new Date(j.startedAt).getTime() : null;
  const arbeit  = Number.isFinite(j?.result?.arbeitMs)  ? j.result.arbeitMs  : null;
  const warten  = Number.isFinite(j?.result?.wartenMs)  ? j.result.wartenMs  : null;
  return { wanduhr, arbeit, warten, massgeblich: arbeit ?? wanduhr };
}

function anteil(j, plan) {
  const { massgeblich } = zeiten(j);
  if (!plan?.intervalMs || !massgeblich || massgeblich <= 0) return null;
  return Math.round((massgeblich / plan.intervalMs) * 1000) / 10;   // Prozent, eine Nachkommastelle
}

// Funktionsdeklaration statt const: dauerText() weiter oben ruft sie, und die
// Reihenfolge der Deklarationen soll dabei keine Rolle spielen.
function msText(ms) {
  if (!Number.isFinite(ms)) return '–';
  // Unter einer Sekunde die Millisekunden zeigen. Auf ganze Sekunden gerundet
  // stand bei syncHistory "0 s" — und das sieht aus wie kaputt, obwohl es der
  // gesunde Zustand ist: alle Monate sind archiviert, der Auftrag prueft nur
  // und ist nach 110 ms fertig.
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} Min ${s % 60} s`;
  return `${Math.floor(s / 3600)} Std ${Math.round((s % 3600) / 60)} Min`;
}

/**
 * Warum Zeilen NICHT in den Nachzuegler kamen.
 *
 * Gemessen am 07.10.2026 schrieb der Strom 37 Zeilen in die Teil-Dateien und
 * der Nachzuegler nahm 12 — die Anzeige nannte nur die 12. Ohne den Grund ist
 * nicht zu unterscheiden, ob das der gesunde Zustand ist (Felder, die der
 * Speicher gar nicht fuehrt) oder ob Nachzuegler und Vollaufbau auseinander
 * laufen (`doppelt`, siehe ereignisSpeicher.ergaenze).
 */
const GRUND_TEXT = {
  feld:      'anderes Feld',
  zeit:      'ohne Zeit',
  geloescht: 'gelöscht',
  grenze:    'vor Projektbeginn',
  doppelt:   'schon vorhanden',
};

function grundText(grund) {
  if (!grund) return '';
  const teile = Object.entries(grund)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${n} ${GRUND_TEXT[k] || k}`);
  return teile.length ? `  ·  verworfen: ${teile.join(', ')}` : '';
}

export default function Diagnose({ onClose }) {
  const [daten,  setDaten]  = useState(null);
  const [fehler, setFehler] = useState(null);

  const laden = useCallback(() => {
    fetch(`${API_BASE}/api/admin/diag/jobs`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setDaten(d); setFehler(null); })
      .catch(e => setFehler(e.message));
  }, []);

  useEffect(() => {
    laden();
    // Nachladen alle 30 s, damit der Dialog beim Beobachten eines Laufs nicht
    // einfriert. Reiner Lesezugriff.
    const t = setInterval(laden, 30000);
    return () => clearInterval(t);
  }, [laden]);

  const stroeme = daten?._stroeme;
  const quelle  = daten?._ereignisQuelle;
  const lesen   = daten?._blobLesen;

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.dialog} onClick={e => e.stopPropagation()}>
        <div style={S.kopf}>
          <h3 style={S.titel}>Diagnose</h3>
          <button style={S.schliessen} onClick={onClose} title="Schliessen">✕</button>
        </div>
        <p style={S.text}>
          Zustand der Hintergrund-Aufträge. Reine Anzeige, aktualisiert sich alle 30 Sekunden.
        </p>

        {fehler && <div style={S.fehler}>Konnte nicht geladen werden: {fehler}</div>}
        {!daten && !fehler && <div style={S.text}>lädt …</div>}

        {daten && (
          <>
            {/* ── Aufträge ──────────────────────────────────────────────── */}
            <div style={S.abschnitt}>Aufträge</div>
            <table style={S.tabelle}>
              <thead>
                <tr>
                  <th style={S.kopfZelle}>Auftrag</th>
                  <th style={S.kopfZelle}>Status</th>
                  <th style={S.kopfZelle}>letzter Lauf</th>
                  <th style={S.kopfZelle}>Dauer</th>
                  <th style={S.kopfZelle}>Fehler</th>
                </tr>
              </thead>
              <tbody>
                {/* Eine leere Tabelle waere genau die stille Anzeige, die wir
                    hier gerade abschaffen: der Taktplan fuellt sich erst, wenn
                    der Worker startet (lokal mit WORKER_ENABLED=false also nie).
                    Dann soll dastehen, warum nichts dasteht. */}
                {auftragsZeilen(daten).length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...S.zelle, color: 'var(--color-warn)' }}>
                      Keine Aufträge gemeldet — läuft der Worker?
                    </td>
                  </tr>
                )}
                {auftragsZeilen(daten).map(([key, name]) => {
                  const j = daten[key];
                  const min  = alterMin(j.finishedAt || j.startedAt);
                  const rot  = j.status === 'error';
                  const grau = j.status === 'idle';
                  const prozent = anteil(j, daten._auftragsplan?.[key]);
                  const zeit    = zeiten(j);
                  return (
                    <tr key={key}>
                      <td style={S.zelleName}>
                        {name}
                        {/* Takt und Versatz kommen vom Server, nicht aus einer
                            Liste hier — sonst stimmen sie nicht mehr, sobald
                            jemand eine Umgebungsvariable setzt.

                            Kein Plan-Eintrag heisst: der Name gehoert keinem
                            Auftrag, sondern einem Admin-Knopf (oder der Worker
                            laeuft lokal nicht). Frueher stand hier ersatzweise
                            ein fest eingetragener Text — der behauptete „20
                            Min" fuer einen 15-Minuten-Auftrag. Lieber ehrlich
                            nichts sagen als falsch. */}
                        <div style={S.takt}>
                          {/* Der Grund kommt vom Server, je Auftrag. Hier stand
                              bis zum 12.10.2026 fest „AUDIT_PFLEGE=aus" — und
                              behauptete das am selben Tag auch fuer
                              syncAgentData, der aus einem anderen Grund
                              stillstand. Ein falsch benannter Grund laedt dazu
                              ein, am falschen Schalter zu drehen. */}
                          {istStillgelegt(daten, key)
                            ? `bewusst abgeschaltet${daten._stillgelegtGrund?.[key] ? ` (${daten._stillgelegtGrund[key]})` : ''}`
                            : taktText(daten._auftragsplan?.[key]) || 'kein fester Takt'}
                        </div>
                        {/* Bei einem Fehler ist die eigentliche Frage nicht
                            „wann war der letzte Versuch", sondern „seit wann
                            geht das schon schief". syncRecentUpdates stand
                            zwoelf Tage auf 422 und sah dabei aus wie ein
                            einmaliger Stolperer. */}
                        {rot && (
                          <div style={{ ...S.takt, color: 'var(--color-warn)' }}>
                            zuletzt erfolgreich: {j.letzterErfolg ? alterText(alterMin(j.letzterErfolg)) : 'nie'}
                          </div>
                        )}
                        {/* Takt und Arbeit sind zwei verschiedene Fragen (U9,
                            08.10.2026). syncFCR tickt alle 15 Minuten und
                            arbeitet um 02:00; syncTrendWeekly tickt stuendlich
                            und arbeitet, wenn eine Woche abgeschlossen ist.
                            „Letzter Lauf" allein saehe nach Dauerbetrieb aus. */}
                        {j.letzteArbeit && (
                          <div style={S.takt}>
                            zuletzt gearbeitet: {alterText(alterMin(j.letzteArbeit))}
                            {j.letzteArbeitMs != null ? `, ${msText(j.letzteArbeitMs)}` : ''}
                          </div>
                        )}
                        {/* Ein leeres `letzteArbeit` ist eine Aussage, kein
                            fehlender Text (13.10.2026). Bisher blieb die Zeile
                            in diesem Fall einfach weg — nicht unterscheidbar von
                            einem Auftrag, der Takt und Arbeit gar nicht trennt.

                            Der Wortlaut ist mit Bedacht eng: „noch KEIN
                            Arbeitslauf aufgezeichnet", nicht „hat noch nie
                            gearbeitet". Die beiden sind nicht dasselbe, und der
                            Unterschied ist belegt — der Admin-Lauf `trendWeekly`
                            vom 09.10.2026 war erfolgreich und steht trotzdem auf
                            `letzteArbeit: null`, weil dieser Weg kein
                            `gearbeitet` zurueckgibt. Wer hier „noch nie
                            gearbeitet" liest, zieht den falschen Schluss.

                            Es heisst: seit es diese Messung gibt, hat der
                            Auftrag keinen Lauf gemeldet, in dem er etwas getan
                            hat. Das ist nachsehenswert, kein Beweis. */}
                        {!j.letzteArbeit && j.result && j.result.gearbeitet === false && (
                          <div style={{ ...S.takt, color: 'var(--color-warn)' }}>
                            noch kein Arbeitslauf aufgezeichnet
                          </div>
                        )}
                      </td>
                      <td style={{ ...S.zelle, color: rot ? 'var(--color-error)' : grau ? 'var(--text-secondary)' : 'var(--color-success)',
                                   fontWeight: rot ? 600 : 400 }}>
                        {istStillgelegt(daten, key) ? 'stillgelegt' :
                         j.status === 'error' ? 'Fehler' : j.status === 'running' ? 'läuft' :
                         nurUngemessen(j) ? 'läuft, nicht gemessen' :
                         j.status === 'idle' ? 'noch nie' :
                         // Ein Auftrag, der getickt hat ohne zu arbeiten, ist nicht
                         // „ok, gerade gelaufen" — er wartet auf seinen Zeitpunkt.
                         (j.result && j.result.gearbeitet === false) ? 'wartet' : 'ok'}
                      </td>
                      {/* Von–bis sekundengenau: damit laesst sich in Grafana
                          nachsehen, was auf dem Pod zu genau diesen Minuten
                          los war. Die Last selbst kann diese Anzeige nicht
                          zeigen. */}
                      <td style={S.zelle}>
                        {j.startedAt ? `${uhr(j.startedAt)} – ${uhr(j.finishedAt)}` : '–'}
                        <div style={S.takt}>
                          {nurUngemessen(j) ? 'keine Aufzeichnung' : alterText(min)}
                        </div>
                      </td>
                      <td style={S.zelle}>
                        {/* Die Arbeit zuerst — sie ist die Zahl, an der eine
                            Takt-Entscheidung haengt. Die Wartezeit darunter
                            erklaert die Differenz zur Wanduhr. */}
                        {zeit.arbeit !== null
                          ? <>{msText(zeit.arbeit)}<span style={S.leise}> Arbeit</span></>
                          : dauerText(j.startedAt, j.finishedAt)}
                        {zeit.warten > 0 && (
                          <div style={S.takt}>+ {msText(zeit.warten)} Wartezeit</div>
                        )}
                        {j.result && j.result.gearbeitet === false && j.result.grund && (
                          <div style={S.takt}>{j.result.grund}</div>
                        )}
                        {prozent !== null && (
                          <div style={{ ...S.takt, color: prozent > 50 ? 'var(--color-warn)' : 'var(--text-secondary)' }}>
                            {prozent} % des Takts
                          </div>
                        )}
                      </td>
                      <td style={{ ...S.zelle, color: 'var(--color-error)' }}>{j.error || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Von Hand ausgelöst ────────────────────────────────────────
                Getrennt von den Aufträgen, weil hier „nie gelaufen" wirklich
                heisst, was es sagt: niemand hat den Knopf gedrückt. In der
                Auftragstabelle wäre dieselbe Zeile eine Fehlermeldung. */}
            {knopfZeilen(daten).length > 0 && (
              <>
                <div style={S.abschnitt}>Von Hand ausgelöst</div>
                <div style={S.kasten}>
                  {knopfZeilen(daten).map(([key, name]) => {
                    const j = daten[key];
                    // Das ALTER des Fehlers gehoert dazu (14.10.2026).
                    // „Nachtraege anstossen" stand nach einem Deploy-Abbruch
                    // rot — und blieb es, waehrend der stuendliche Auftrag
                    // daneben seinen ganzen Rueckstand aufholte. Eine rote
                    // Zeile ohne Alter liest sich wie „gerade kaputt";
                    // dieselbe Zeile mit „vor 20 Std" liest sich richtig. Ein
                    // Knopf wird nicht von selbst wieder gruen, also muss die
                    // Anzeige den Unterschied tragen.
                    //
                    // Der Grund gehoert daneben (13.10.2026). Hier stand nur das
                    // Wort „Fehler" — waehrend die Meldung die ganze Zeit im
                    // selben Datensatz lag (jobStatus.failJob schreibt sie nach
                    // `error`). Die Auftragstabelle zeigt sie, dieser Block
                    // nicht: ein Knopf, der rot wird und nicht sagt warum,
                    // zwingt zum Raten.
                    return (
                      <Zeile
                        key={key}
                        name={name}
                        wert={j.status === 'error'
                            ? `Fehler ${alterText(alterMin(j.finishedAt || j.startedAt))}${j.error ? ` — ${j.error}` : ' (ohne Meldung)'}`
                          : j.status === 'running' ? 'läuft gerade'
                          : j.startedAt
                            ? `${uhr(j.startedAt)}, ${alterText(alterMin(j.finishedAt || j.startedAt))}${ergebnisText(j.result)}`
                          : 'nie gedrückt'}
                        rot={j.status === 'error'}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {/* ── Ströme ────────────────────────────────────────────────── */}
            {stroeme && (
              <>
                <div style={S.abschnitt}>Ströme</div>
                <div style={S.kasten}>
                  <Zeile name="Gesamtzustand" wert={stroeme.zustand}
                         rot={stroeme.zustand !== 'ok'} />
                  <Zeile name="Ereignisse"  wert={`${stroeme.ereignisse?.alterMinuten} Min alt`} />
                  <Zeile name="Tickets"     wert={`${stroeme.tickets?.alterMinuten} Min alt`} />
                  <Zeile name="Umformung"   wert={`${stroeme.umgeformt?.alterMinuten} Min alt, ${stroeme.umgeformt?.teile} Teile`} />
                </div>

                {/* ── Woraus die Laufzeit besteht ────────────────────────────
                    `syncStroeme` liefert diese Aufschluesselung seit dem
                    02.10.2026 zurueck, `trackJobStatus` legt sie als `result`
                    ab — angezeigt hat sie nie jemand.

                    Sie wird gebraucht, sobald man ueber den Takt entscheidet:
                    ein Lauf besteht aus Warten auf das Zendesk-Kontingent,
                    Einsortieren und dem Neuaufbau des Abfrage-Speichers. Nur
                    der letzte Posten waechst mit der Taktfrequenz — er baut
                    jedes Mal alle Zeilen neu. Ob sich dafuer ein
                    Nachzuegler-Speicher lohnt (siehe docs/WORKER-INVENTAR.md,
                    Zielbild), entscheidet genau diese Zahl. */}
                {daten.syncStroeme?.result && (
                  <div style={S.kasten}>
                    <Zeile name="Seiten geholt"
                           wert={`Ereignisse ${daten.syncStroeme.result.seitenEreignisse ?? '–'} · Tickets ${daten.syncStroeme.result.seitenTickets ?? '–'}`} />
                    {daten.syncStroeme.result.einsortiert && (
                      <Zeile name="eingelesen"
                             wert={`${daten.syncStroeme.result.einsortiert.zeilenNeu} Zeilen`}
                             unten={`${daten.syncStroeme.result.einsortiert.seiten} Seiten · ${daten.syncStroeme.result.einsortiert.teile} Teile geschrieben`} />
                    )}
                    {/* Tickets, die nur ueber den Zustands-Speicher zugeordnet
                        werden konnten. Jedes davon waere vorher still
                        verlorengegangen — am 12.10.2026 hat genau diese Luecke
                        34 Tickets gekostet, und nichts hat es gemeldet.
                        Dauerhaft > 0 heisst: in der Landezone geht etwas
                        verloren, und der Rueckgriff deckt es nur zu. */}
                    {daten.syncStroeme.result.einsortiert?.gerettet > 0 && (
                      <Zeile name="ohne Anlage zugeordnet"
                             wert={`${daten.syncStroeme.result.einsortiert.gerettet} Tickets`}
                             unten="über den Zustands-Speicher gerettet — wären sonst verloren"
                             rot />
                    )}
                    {/* ── „fehlend" ist fast immer harmlos ──────────────────
                        Die meisten fehlenden Tickets sind laengst beurteilt:
                        geloescht oder vor dem Projektbeginn angelegt. Sie
                        stehen dauerhaft in meta/abgleich-ausserhalb.json und
                        werden nicht erneut bei Zendesk erfragt.

                        Die erste Fassung dieser Zeile zeigte nur „613 fehlend"
                        und las sich wie ein Alarm, wo der gesunde Zustand
                        stand. Interessant ist allein der REST: was fehlt, ohne
                        dass es dafuer einen Grund gibt. */}
                    {daten.syncStroeme.result.abgeglichen && (() => {
                      const ab = daten.syncStroeme.result.abgeglichen;
                      const offen = Math.max(0, (ab.fehlend || 0) - (ab.zuRecht || 0) - (ab.nachgetragen || 0));
                      return (
                        <Zeile name="Abgleich"
                               wert={offen > 0 ? `${offen} ungeklärt` : 'ohne Befund'}
                               unten={`${ab.imStrom} geprüft · ${ab.fehlend} fehlend, davon ${ab.zuRecht} zu Recht` +
                                      (ab.nachgetragen ? ` · ${ab.nachgetragen} nachgetragen` : '') +
                                      // Seit dem 08.10.2026 liest der Abgleich nur noch
                                      // NEUE Seiten. Steht hier dauerhaft „326 von 326",
                                      // greift das Gedaechtnis nicht und er liest wieder
                                      // alles — dann kostet er 5 der 13 Sekunden.
                                      (ab.seiten != null
                                        ? ` · ${ab.seiten}${ab.seitenImFenster != null ? ` von ${ab.seitenImFenster}` : ''} Seiten gelesen`
                                        : '') +
                                      (ab.ms != null ? ` · ${msText(ab.ms)}` : '')}
                               rot={offen > 0} />
                      );
                    })()}
                    {/* ── Die zweite Wache (R12·3, 14.10.2026) ──────────────
                        Der Abgleich darueber prueft Ticket-Strom gegen
                        Zustands-Speicher. Diese hier prueft, ob alles, was in
                        der Landezone liegt, auch in tickets-2026-v2 ankommt.
                        Genau dort gingen am 12.10. 34 Tickets verloren — und
                        gemeldet hat es nichts, aufgefallen ist es beim
                        Vermessen des KI-Bots.
                        Sie laeuft auf demselben Durchgang mit und kostet
                        keinen zusaetzlichen Lesezugriff. */}
                    {daten.syncStroeme.result.abgeglichen?.umformung && (() => {
                      const u = daten.syncStroeme.result.abgeglichen.umformung;
                      if (u.fehler) {
                        return <Zeile name="Umformung geprüft" wert={`nicht möglich — ${u.fehler}`} rot />;
                      }
                      // `zuRecht` getrennt ausweisen (14.10.2026): geloescht,
                      // oder vor Projektbeginn angelegt. Ohne diese Zeile sah
                      // man nur „12 fehlen" und musste jedes Mal von Hand bei
                      // Zendesk nachsehen, ob es ein Befund ist — genau das
                      // hat an diesem Tag zweimal eine Stunde gekostet.
                      return (
                        <Zeile name="Umformung geprüft"
                               wert={u.fehlend > 0 ? `${u.fehlend} fehlen` : 'vollständig'}
                               unten={`${u.geprueft} Tickets aus der Landezone` +
                                      (u.frisch ? ` · ${u.frisch} noch unterwegs` : '') +
                                      (u.zuRecht ? ` · ${u.zuRecht} fehlen zu Recht (gelöscht oder vor Projektbeginn)` : '') +
                                      (u.beispiele?.length ? ` · z. B. #${u.beispiele.slice(0, 3).join(' #')}` : '')}
                               rot={u.fehlend > 0} />
                      );
                    })()}
                    {/* Seit dem 07.10.2026 ist das nicht mehr der Posten, der
                        mit dem Takt mitwaechst: der Nachzuegler nimmt die
                        neuen Zeilen in Millisekunden auf. `art` sagt, welcher
                        Weg gegangen wurde — steht dort dauerhaft
                        „vollaufbau", greift der Rueckfall und der Umbau
                        bringt nichts. */}
                    <Zeile name="Abfrage-Speicher nachgeführt"
                           wert={daten.syncStroeme.result.nachgeladen
                             ? msText(daten.syncStroeme.result.nachgeladen.ms)
                             : 'nicht nötig — nichts Neues'}
                           unten={daten.syncStroeme.result.nachgeladen?.art
                             ? (daten.syncStroeme.result.nachgeladen.art === 'nachzuegler'
                                 ? `Nachzügler · ${daten.syncStroeme.result.nachgeladen.aufgenommen} von ` +
                                   `${daten.syncStroeme.result.nachgeladen.zeilenNeu} aufgenommen` +
                                   grundText(daten.syncStroeme.result.nachgeladen.grund)
                                 : 'VOLLAUFBAU — Rückfall hat gegriffen')
                             : null}
                           rot={daten.syncStroeme.result.nachgeladen?.art === 'vollaufbau'} />
                  </div>
                )}

                {/* ── Der Abfrage-Speicher selbst ──────────────────────────
                    Wie voll der Nachzügler ist, muss man sehen können: wächst
                    er unerwartet, ist entweder die Obergrenze falsch gewählt
                    oder der Nachtrag reicht Zeilen durch, die er nicht
                    sollte. Ein Mechanismus, den niemand beobachten kann, ist
                    genau die stille Lücke, die uns diese Woche dreimal
                    getroffen hat. */}
                {daten._abfrageSpeicher && (
                  <>
                    <div style={S.abschnitt}>Abfrage-Speicher</div>
                    <div style={S.kasten}>
                      <Zeile name="Grundspeicher"
                             wert={`${daten._abfrageSpeicher.zeilen} Zeilen`}
                             unten={`${daten._abfrageSpeicher.tickets} Tickets · ${daten._abfrageSpeicher.dateien} Teile · zuletzt gebaut in ${msText(daten._abfrageSpeicher.ladeMs)}`} />
                      <Zeile name="Nachzügler"
                             wert={`${daten._abfrageSpeicher.nachzuegler?.zeilen ?? '–'} Zeilen`}
                             unten={daten._abfrageSpeicher.nachzuegler
                               ? `${daten._abfrageSpeicher.nachzuegler.tickets} Tickets · Obergrenze ${daten._abfrageSpeicher.nachzuegler.grenze}`
                               : null}
                             rot={(daten._abfrageSpeicher.nachzuegler?.zeilen ?? 0)
                                  > (daten._abfrageSpeicher.nachzuegler?.grenze ?? Infinity) * 0.8} />
                      <Zeile name="gelöschte Tickets"
                             wert={daten._abfrageSpeicher.geloeschteTickets} />
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Zendesk-Last ──────────────────────────────────────────── */}
            {daten._zendesk && (
              <>
                <div style={S.abschnitt}>Zendesk-Abrufe (seit {daten._zendesk.seitMinuten} Min)</div>
                <div style={S.kasten}>
                  <Zeile name="gesamt" wert={`${daten._zendesk.gesamt}  ·  ${daten._zendesk.proStunde}/Std`} />
                  {/* Nach Familie — "audits" ist die, die mit der Umstellung
                      verschwinden MUSS. Faellt sie nicht, war der Umbau fuer
                      die Last umsonst. */}
                  {Object.entries(daten._zendesk.familie)
                    .sort((a, b) => b[1].gesamt - a[1].gesamt)
                    .map(([f, v]) => (
                      <Zeile key={f} name={`  ${f}`} wert={`${v.gesamt}  ·  ${v.proStunde}/Std`} />
                    ))}
                  {/* Zeit in UNSERER Drossel: solange sie klein bleibt, ist
                      Luft. Steigt sie, bremst uns das eigene Kontingent. */}
                  <Zeile name="in der Drossel gewartet" wert={`${daten._zendesk.drosselSekunden} s`} />
                  <Zeile name="429 von Zendesk" wert={daten._zendesk.fehler429}
                         rot={daten._zendesk.fehler429 > 0} />
                </div>

                {/* ── Wer ruft an ────────────────────────────────────────────
                    Die Familie sagt wofuer, nicht wer. An dieser Luecke ist die
                    Worker-Analyse vom 06.10.2026 dreimal gescheitert: welcher
                    Auftrag die Suchen treibt, war nicht zu beantworten.

                    "Seitenaufruf" sammelt alles, was aus keinem Auftrag kam —
                    Dashboard, Wallboard, Admin. Wie viel Last die Ansichten
                    selbst erzeugen, wusste bis dahin ebenfalls niemand. */}
                {daten._zendesk.auftrag && Object.keys(daten._zendesk.auftrag).length > 0 && (
                  <>
                    <div style={S.abschnitt}>davon je Auftrag</div>
                    <div style={S.kasten}>
                      {Object.entries(daten._zendesk.auftrag).map(([name, v]) => (
                        <Zeile
                          key={name}
                          name={BESCHRIFTUNG[name] || name}
                          wert={`${v.gesamt}  ·  ${v.proStunde}/Std`}
                          unten={Object.entries(v.familie)
                            .map(([f, n]) => `${f} ${n}`).join(' · ')}
                        />
                      ))}
                    </div>
                  </>
                )}
                {/* Seit 16.10.2026: wer wartet wie lange worauf — je Rolle,
                    nicht je Person. Gespeichert alle 10 Minuten als
                    Tagesdatei, ausgewertet ueber /api/admin/diag/nutzung. */}
                {daten._nutzung && Object.keys(daten._nutzung.rollen || {}).length > 0 && (
                  <>
                    <div style={S.abschnitt}>Nutzung heute je Rolle (seit Prozessstart)</div>
                    <div style={S.kasten}>
                      {Object.entries(daten._nutzung.rollen).map(([rolle, v]) => (
                        <Zeile key={rolle} name={rolle}
                               wert={`${v.n} Aufrufe`}
                               unten={`${v.nutzer} verschiedene Nutzer`} />
                      ))}
                      {(daten._nutzung.nachZeit || []).map(z => (
                        <Zeile
                          key={`${z.rolle}|${z.ansicht}|${z.zeitraum}`}
                          name={`${z.rolle} · ${z.ansicht} · ${z.zeitraum}`}
                          wert={`${z.n} · Ø ${z.oeMs} ms · max ${z.msMax} ms`}
                          unten={[
                            `zusammen ${z.sekundenGesamt} s`,
                            z.ueber2s ? `${z.ueber2s} über 2 s` : null,
                            z.ueber5s ? `${z.ueber5s} über 5 s` : null,
                            z.abgebrochen ? `${z.abgebrochen} abgebrochen` : null,
                            z.fehler ? `${z.fehler} Fehler` : null,
                            z.zendesk ? `Zendesk ${z.zendesk}` : null,
                            z.wiederholt ? `${z.wiederholt} Wiederholungen < 5 Min` : null,
                            `${z.nutzer ?? 0} Nutzer`,
                          ].filter(Boolean).join(' · ')}
                          rot={z.ueber5s > 0 || z.abgebrochen > 0 || z.fehler > 0}
                        />
                      ))}
                      <Zeile name="gespeichert"
                             wert={daten._nutzung.speichern?.letzterLauf
                               ? `${daten._nutzung.speichern.geschrieben}× · zuletzt ${new Date(daten._nutzung.speichern.letzterLauf).toLocaleTimeString('de-CH')}`
                               : 'noch nicht (alle 10 Min)'}
                             unten={daten._nutzung.speichern?.fehler
                               ? `${daten._nutzung.speichern.fehler} Fehler — ${daten._nutzung.speichern.letzterFehler?.text || ''}`
                               : undefined}
                             rot={daten._nutzung.speichern?.fehler > 0} />
                    </div>
                  </>
                )}
                {/* Seit 16.10.2026: „Seitenaufruf" nach Endpunkt. Der groesste
                    verbliebene Zendesk-Weg war bis dahin eine einzige Zahl. */}
                {daten._zendesk.seite && Object.keys(daten._zendesk.seite).length > 0 && (
                  <>
                    <div style={S.abschnitt}>Seitenaufrufe je Endpunkt</div>
                    <div style={S.kasten}>
                      {Object.entries(daten._zendesk.seite).map(([name, v]) => (
                        <Zeile
                          key={name}
                          name={name}
                          wert={`${v.gesamt}  ·  ${v.proStunde}/Std`}
                          unten={Object.entries(v.familie)
                            .map(([f, n]) => `${f} ${n}`).join(' · ')}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Aufwaermen nach dem Start ───────────────────────────────
                Bis die Speicher stehen, zahlt der erste Besucher die Ladezeit
                selbst. Vor dem 09.10.2026 lud sie beim Start gar nichts — der
                erste Ladevorgang kam mit dem Strom-Auftrag bei +2,5 Minuten. */}
            {daten._aufwaermen && daten._aufwaermen.gestartet && (
              <>
                <div style={S.abschnitt}>Speicher aufgewärmt</div>
                <div style={S.kasten}>
                  <Zeile name="Dauer nach dem Start"
                         wert={daten._aufwaermen.laeuft ? 'läuft gerade …'
                           : daten._aufwaermen.ms != null ? msText(daten._aufwaermen.ms) : '–'}
                         unten={Object.entries(daten._aufwaermen.speicher || {})
                           .map(([k, v]) => `${k} ${v != null ? msText(v) : 'FEHLER'}`).join(' · ') || null}
                         rot={!!daten._aufwaermen.fehler} />
                  {daten._aufwaermen.fehler && (
                    <Zeile name="Fehler" wert={daten._aufwaermen.fehler} rot />
                  )}
                </div>
              </>
            )}

            {/* ── Speicher ──────────────────────────────────────────────── */}
            {daten._speicher && (
              <>
                <div style={S.abschnitt}>Speicher</div>
                <div style={S.kasten}>
                  <Zeile name="Verbrauch (RSS)" wert={`${daten._speicher.rssMb} MB`}
                         rot={daten._speicher.anteilProzent >= 85} />
                  {daten._speicher.limitMb && (
                    <Zeile name="Grenze des Containers" wert={`${daten._speicher.limitMb} MB`} />
                  )}
                  {daten._speicher.anteilProzent !== null && (
                    <Zeile name="ausgelastet" wert={`${daten._speicher.anteilProzent} %`}
                           rot={daten._speicher.anteilProzent >= 85} />
                  )}
                  <Zeile name="davon Heap" wert={`${daten._speicher.heapMb} MB`} />
                </div>
              </>
            )}

            {/* ── Ereignisquelle ────────────────────────────────────────── */}
            {quelle && (
              <>
                <div style={S.abschnitt}>Ereignisquelle</div>
                <div style={S.kasten}>
                  {/* Beim Umschalten die wichtigste Zeile: springt `leer`, kennt
                      der Strom Tickets nicht, die der Blob kannte. */}
                  <Zeile name="aktive Quelle" wert={quelle.quelle === 'strom' ? 'Ereignisstrom' : 'Audit-Blobs'} />
                  {/* Woher die Agenten-Daten ihre Kandidaten nehmen. Steht hier
                      „Zendesk-Suche", laeuft der alte Weg mit drei Suchen je Agent —
                      und die dritte Quelle (agent-activity-index) ist tot: der
                      Auftrag, der sie schrieb, ist am 13.10.2026 entfernt worden. */}
                  {daten._agentenPool && (
                    <Zeile name="Agenten-Pool"
                           wert={daten._agentenPool.quelle === 'strom' ? 'Autoren-Index' : 'Zendesk-Suche'}
                           rot={daten._agentenPool.quelle !== 'strom' && quelle.quelle === 'strom'} />
                  )}
                  {/* Die neun team-relevanten Abteilungen: neun Zendesk-Suchen
                      mit adaptiver Fensterung, oder derselbe Filter auf dem
                      Zustands-Speicher. Gemessen 66 s gegen 16 ms, 0 Verluste. */}
                  {daten._teamPool && (
                    <Zeile name="Team-Pool"
                           wert={daten._teamPool.quelle === 'strom' ? 'Zustands-Speicher' : 'Zendesk-Suche'}
                           rot={daten._teamPool.quelle !== 'strom' && quelle.quelle === 'strom'} />
                  )}
                  {/* Mit „Zustands-Speicher" stellt der KI-Bot-Block NULL
                      Zendesk-Anfragen. Vorher waren es über hundert je kaltem
                      Aufruf — und keine davon tauchte in einem Zähler auf,
                      weil kiBotService axios direkt benutzt (R13). Diese Zeile
                      ist das Einzige, woran man von aussen sieht, welcher Weg
                      läuft. */}
                  {daten._kiBotPool && (
                    <Zeile name="KI-Bot-Pool"
                           wert={daten._kiBotPool.quelle === 'strom' ? 'Zustands-Speicher' : 'Zendesk-Suche'}
                           rot={daten._kiBotPool.quelle !== 'strom' && quelle.quelle === 'strom'} />
                  )}
                  {/* Welche Gruppe ueber den Nenner der Abdeckungs-Quote
                      entscheidet. „bei Eingang" heisst: ein weitergeleitetes
                      Ticket bleibt in der Auswertung. Rot, wenn die
                      Einstellung wirkungslos ist — die Zendesk-Suche kennt
                      die Eingangs-Gruppe nicht. */}
                  {daten._kiBotPool?.gruppe && (
                    <Zeile name="Abdeckung zählt"
                           wert={daten._kiBotPool.gruppe === 'eingang'
                             ? 'Gruppe bei Eingang'
                             : 'aktuelle Gruppe'}
                           rot={daten._kiBotPool.gruppe === 'eingang' && daten._kiBotPool.quelle !== 'strom'} />
                  )}
                  {/* Nach welcher Regel ein Solve als Erstkontakt-Loesung
                      zaehlt (A6). „Bestand" heisst: die FCR-Kachel und der
                      Jahresverlauf rechnen verschieden — gemessen 13 %
                      Unterschied ueber den September 2026. */}
                  {daten._fcrPool?.regel && (
                    <Zeile name="FCR-Regel"
                           wert={daten._fcrPool.regel === 'fachregel'
                             ? 'eine Regel (Fachregel)'
                             : 'Bestand — zwei Regeln'}
                           rot={daten._fcrPool.regel !== 'fachregel'} />
                  )}
                  {daten._fcrPool && (
                    <Zeile name="FCR-Pool"
                           wert={daten._fcrPool.quelle === 'strom' ? 'Zustands-Speicher' : 'Zendesk-Suche'}
                           rot={daten._fcrPool.quelle !== 'strom' && quelle.quelle === 'strom'} />
                  )}
                  {/* Rechnet die Agenten-Ansicht auf Abruf oder liest sie die vom
                      Worker geschriebenen Pools? „auf Abruf" heisst: so frisch wie
                      der Strom statt bis zu fuenf Minuten alt. */}
                  {daten._ansichtQuelle && (
                    <Zeile name="Agenten-Ansicht"
                           wert={daten._ansichtQuelle.quelle === 'strom' ? 'auf Abruf gerechnet' : 'vorgerechnet (Blob)'} />
                  )}
                  <Zeile name="Abrufe"        wert={`${quelle.audit} Blob · ${quelle.strom} Strom`} />
                  <Zeile name="ohne Treffer"  wert={quelle.leer} />
                  <Zeile name="Fehler"        wert={quelle.fehler} rot={quelle.fehler > 0} />
                </div>
              </>
            )}

            {/* ── Live-Wege ─────────────────────────────────────────────────
                Beide Wege (Agenten-Ansicht und Monats-Pool) weichen bei einem
                Fehler STILL auf den Blob aus. Das ist richtig so — lieber der
                vorgerechnete Stand als gar keiner. Aber ohne diese Anzeige
                waere es unsichtbar: die Zahlen im Dashboard sind dann nicht
                falsch, sondern veraltet, und das sieht man ihnen nicht an.
                „zurueckgefallen" muss 0 sein. Alles andere gehoert angesehen. */}
            {daten._poolZaehler && (
              <>
                <div style={S.abschnitt}>Live-Wege</div>
                <div style={S.kasten}>
                  <Zeile name="Agenten-Ansicht"
                         wert={`${daten._poolZaehler.ansicht.strom} auf Abruf · ${daten._poolZaehler.ansicht.blob} Blob`} />
                  <Zeile name="Monats-Pool"
                         wert={daten._poolZaehler.monatsPool === 'strom'
                           ? `${daten._poolZaehler.monat.strom} auf Abruf · ${daten._poolZaehler.monat.blob} Blob`
                           : `${daten._poolZaehler.monat.blob} Blob (Umschalter steht auf blob)`} />
                  {/* Die ergaenzenden Team-Tickets der Ticket-Analyse (U11c).
                      Mit „auf Abruf" liest `meta/supplementary-team-tickets`
                      niemand mehr — dann ist syncSupplementaryTeamTickets ein
                      Schreiber ohne Leser und darf abgeschaltet werden. Vorher
                      nicht. */}
                  {daten._poolZaehler.ergaenzung && (
                    <Zeile name="Ergänzende Team-Tickets"
                           wert={daten._poolZaehler.teamErgaenzung === 'strom'
                             ? `${daten._poolZaehler.ergaenzung.strom} auf Abruf · ${daten._poolZaehler.ergaenzung.blob} Blob`
                             : `${daten._poolZaehler.ergaenzung.blob} Blob (Umschalter steht auf blob)`} />
                  )}
                  {/* U4 (15.10.2026): der erste Leser des Monats-Archivs auf
                      dem Strom. Der Von-Bis-Zeitraum liest mit „auf Abruf"
                      nicht mehr aus `tickets-v2`, sondern rechnet den Monat
                      aus den Ereignissen — und zwar zum Stand des
                      MONATSENDES, nicht von heute.

                      Erst wenn ALLE Leser des Archivs so dastehen, darf
                      `syncHistory` abgeschaltet und `tickets-v2` geloescht
                      werden. Vorher nicht: die ubrigen lesen weiter daraus. */}
                  {daten._poolZaehler.monatsArchiv && (
                    <Zeile name="Monats-Archiv (Von–Bis)"
                           wert={daten._poolZaehler.monatsQuelle === 'archiv'
                             ? `${daten._poolZaehler.monatsArchiv.archiv} Archiv (Umschalter steht auf archiv)`
                             : `${daten._poolZaehler.monatsArchiv.strom} auf Abruf · ${daten._poolZaehler.monatsArchiv.archiv} Archiv (Umschalter: ${daten._poolZaehler.monatsQuelle})`} />
                  )}
                  {/* U4 (15.10.2026): was liegt im Monats-Zwischenspeicher?
                      Ein abgeschlossener Monat bleibt bis zu zwoelf Stunden
                      liegen — neue Ereignisse tragen den Zeitstempel von
                      heute und koennen ihn nicht mehr veraendern. Der
                      laufende Monat verfaellt dagegen, sobald der Strom
                      vorrueckt.

                      Die Ticketzahl je Eintrag ist die Grundlage fuer die
                      Frage, ob `STROM_MONATE_RAM` erhoeht werden darf, damit
                      die Jahr-Ansicht ihre neun Monate halten kann. */}
                  {daten._monatsSpeicher && (
                    <Zeile name="Monats-Zwischenspeicher"
                           wert={daten._monatsSpeicher.eintraege?.length
                             ? `${daten._monatsSpeicher.eintraege.map(e =>
                                 `${e.eintrag} ${e.tickets} Tickets (${e.alterMin} Min${e.fertig ? ', fertig' : ''})`
                               ).join(' · ')} — Grenze ${daten._monatsSpeicher.grenze}`
                             : `leer — Grenze ${daten._monatsSpeicher.grenze} Monate`} />
                  )}
                  <Zeile name="zurückgefallen"
                         wert={rueckfaelle(daten._poolZaehler)}
                         rot={rueckfaelle(daten._poolZaehler) > 0} />
                  {/* Schreibt der Worker die Pools ueberhaupt noch vor? Mit
                      „nein" ist der Rueckweg auf blob NICHT mehr sofort
                      aktuell — die Blobs veralten ab dem Abschalten. */}
                  {daten._agentenDaten && (
                    <Zeile name="Worker rechnet vor"
                           wert={daten._agentenDaten.laeuft ? 'ja (syncAgentData läuft)' : 'nein — abgeschaltet'} />
                  )}
                  {/* Diese Warnung bedeutet: eine Ansicht liest Dateien, die
                      niemand mehr schreibt. Kein Fehler, keine leere Seite —
                      nur Zahlen, die stillstehen. */}
                  {daten._agentenDaten && daten._agentenDaten.warnungen.map((w, i) => (
                    <Zeile key={i} name="ACHTUNG" wert={w} rot />
                  ))}
                  {daten._poolZaehler.letzterFehler && (
                    <Zeile name="letzter Fehler"
                           wert={`${daten._poolZaehler.letzterFehler.wo}: ${daten._poolZaehler.letzterFehler.text}`}
                           rot />
                  )}
                </div>
              </>
            )}

            {/* ── Blob-Zugriffe ─────────────────────────────────────────── */}
            {lesen && (
              <>
                <div style={S.abschnitt}>Blob-Lesezugriffe</div>
                <div style={S.kasten}>
                  <Zeile name="Versuche"       wert={lesen.versuche} />
                  <Zeile name="wiederholt"     wert={lesen.wiederholt} />
                  <Zeile name="aufgegeben"     wert={lesen.aufgegeben} rot={lesen.aufgegeben > 0} />
                  {/* Ein aufgegebener Lesezugriff liefert dem Aufrufer `null` —
                      also dasselbe wie ein Blob, den es nicht gibt. Ohne den
                      Pfad ist nicht zu sagen, welche Zahl im Dashboard deshalb
                      fehlt. */}
                  {lesen.letzterLeseFehler && (
                    <Zeile name="letzter Lesefehler"
                           wert={`${lesen.letzterLeseFehler.pfad} — ${lesen.letzterLeseFehler.text}`}
                           rot />
                  )}
                  <Zeile name="nicht gefunden" wert={lesen.nichtGefunden} />
                </div>

                {/* Die Schreibseite hatte bis zum 06.10.2026 gar keinen Zaehler.
                    Deshalb blieb unsichtbar, dass zwischen dem 02. und 06.10.
                    Teile des Abfrage-Speichers nicht geschrieben wurden —
                    waehrend die Auftraege gruen meldeten. Diese Zeile muss 0
                    bleiben; steht dort etwas, fehlen Daten. */}
                <div style={S.abschnitt}>Blob-Schreibzugriffe</div>
                <div style={S.kasten}>
                  <Zeile name="geschrieben"    wert={lesen.geschrieben} />
                  <Zeile name="fehlgeschlagen" wert={lesen.schreibFehler} rot={lesen.schreibFehler > 0} />
                  {/* Am 12.10.2026 stand hier nach 65 Stunden „1" — und mehr war
                      nicht herauszufinden. Welcher Blob und warum stand nur im
                      Pod-Protokoll. Eine Zahl ohne den letzten Fall sagt nicht,
                      ob es ein Aussetzer war oder ein Muster.
                      „nicht gemeldet" heisst: der Aufrufer hat es nicht erfahren
                      und haelt den Schreibvorgang fuer erledigt. */}
                  {lesen.letzterSchreibFehler && (
                    <>
                      <Zeile name="letzter Schreibfehler"
                             wert={`${lesen.letzterSchreibFehler.pfad} — ${lesen.letzterSchreibFehler.text}`}
                             rot />
                      <Zeile name="davon erfuhr der Aufrufer"
                             wert={lesen.letzterSchreibFehler.strikt ? 'ja (Fehler geworfen)' : 'nein — still'}
                             rot={!lesen.letzterSchreibFehler.strikt} />
                    </>
                  )}
                </div>

                {/* ── Welcher Speicher kostet die Zugriffe ───────────────────
                    Die Gesamtzahl sagt, DASS gelesen wird, nicht WORAUS. Nach
                    der Umstellung fielen die Lesezugriffe von 944 auf 53 je
                    Minute — aber welcher Speicher die verbliebenen verursacht,
                    war nicht zu beantworten.

                    Davon haengt der naechste Umbau ab: ist es
                    `tickets-2026-v2`, erledigt sie der Nachzuegler-Speicher
                    mit; sind es `agent-tickets`, `tickets-v2` oder
                    `trend-weekly`, braucht es den Umbau der
                    Materialisierungen. */}
                {lesen.jeBereich && Object.keys(lesen.jeBereich).length > 0 && (
                  <>
                    <div style={S.abschnitt}>davon je Bereich</div>
                    <div style={S.kasten}>
                      {Object.entries(lesen.jeBereich).map(([bereich, v]) => (
                        <Zeile
                          key={bereich}
                          name={bereich}
                          wert={`${v.gelesen} gelesen`}
                          unten={[
                            v.geschrieben ? `${v.geschrieben} geschrieben` : null,
                            v.nichtGefunden ? `${v.nichtGefunden} nicht gefunden` : null,
                          ].filter(Boolean).join(' · ') || null}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** `unten` steht klein unter dem Namen — fuer die Aufschluesselung einer Zeile. */
function Zeile({ name, wert, rot, unten }) {
  return (
    <div style={S.kastenZeile}>
      <span style={S.kastenName}>
        {name}
        {unten && <div style={S.takt}>{unten}</div>}
      </span>
      <span style={{ ...S.kastenWert, color: rot ? 'var(--color-error)' : 'var(--text-primary)', fontWeight: rot ? 600 : 400 }}>
        {String(wert ?? '–')}
      </span>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dialog:   { width: 'min(720px, 94vw)', maxHeight: '88vh', overflowY: 'auto',
              background: 'var(--bg-card)', color: 'var(--text-primary)',
              borderRadius: 10, padding: '18px 20px 20px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' },
  kopf:     { display: 'flex', alignItems: 'center', marginBottom: 6 },
  titel:    { fontSize: 16, fontWeight: 600, margin: 0 },
  schliessen: { marginLeft: 'auto', border: 'none', background: 'transparent',
                fontSize: 15, cursor: 'pointer', color: 'var(--text-secondary)' },
  text:     { fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '4px 0 14px' },
  fehler:   { fontSize: 13, color: 'var(--color-error)', marginBottom: 12 },
  abschnitt:{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
              color: 'var(--text-secondary)', margin: '16px 0 6px' },
  tabelle:  { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  kopfZelle:{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.03em', color: 'var(--text-secondary)',
              padding: '0 10px 4px 0', whiteSpace: 'nowrap' },
  // Farben IMMER ueber die Theme-Variablen — das Dashboard hat einen hellen und
  // einen dunklen Modus, und fest eingetragene Werte stimmen hoechstens in
  // einem von beiden.
  zelleName:{ padding: '7px 10px 7px 0', verticalAlign: 'top', color: 'var(--text-primary)',
              borderTop: '1px solid var(--border)' },
  takt:     { fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 },
  zelle:    { padding: '7px 10px 7px 0', verticalAlign: 'top', whiteSpace: 'nowrap',
              color: 'var(--text-primary)', borderTop: '1px solid var(--border)' },
  kasten:   { background: 'var(--bg-secondary)', borderRadius: 6, padding: '8px 12px' },
  kastenZeile: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 },
  kastenName:  { color: 'var(--text-secondary)' },
  kastenWert:  { fontVariantNumeric: 'tabular-nums' },
  leise:    { color: 'var(--text-secondary)', fontWeight: 400 },
};
